/* eslint-disable react-refresh/only-export-components */
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { useApiData } from "./ApiData";
import { GoalContext, ImprovementData, ImprovementGoal, ImprovementSurvey, Survey } from "@/types";
import { scoreResult2ScoresObject } from "@/lib/mapping";
import getQuestionsForSurveyType from "../lib/getQuestionsForSurveyType";

type ImprovementGoalContextType = GoalContext & {
  reloadGoal: () => Promise<void>;
  setGoal: (goal: ImprovementGoal | null) => void;
};

const ImprovementGoalContext = createContext<ImprovementGoalContextType>({
  goal: null,
  originalSurvey: null,
  improvementData: null,
  recentSurveys: null,
  questions: [],
  reloadGoal: async () => {},
  setGoal: () => {},
});

type ImprovementGoalProviderProps = {
  children: ReactNode;
  goalId?: string;
  surveyId?: string;
};

export const useImprovementGoal = () => {
  return useContext(ImprovementGoalContext);
};

export const ImprovementGoalProvider: React.FC<ImprovementGoalProviderProps> = ({ children, surveyId, goalId }) => {
  const { get } = useApiData();
  const [goal, setGoal] = useState<ImprovementGoal | null>(null);
  const [improvementData, setImprovementData] = useState<ImprovementData | null>(null);
  const [recentSurveys, setRecentSurveys] = useState<ImprovementSurvey[]>([]);
  const [originalSurvey, setOriginalSurvey] = useState<Survey | null>(null);
  const [questions, setQuestions] = useState<string[]>([]);

  const reloadGoal = useCallback(async () => {
    if (!goalId) {
      return;
    }
    const result = await get(`/goals/${goalId}`);
    if (result) {
      setGoal(result);
    }
  }, [get, goalId]);

  useEffect(() => {
    const getImprovementData = async () => {
      if (!(surveyId || goal?.originalSurvey)) {
        return;
      }
      const result: ImprovementData = await get(`/survey/${surveyId || goal?.originalSurvey}/improvement-data`);
      if (!result) {
        return;
      }
      const { surveyName, answers, accessibleBy, startDate, endDate, surveyType } = result;
      if (answers) {
        const scoreObj = scoreResult2ScoresObject(answers);
        setImprovementData({ surveyName, answers, accessibleBy, surveyType, startDate, endDate, ...scoreObj });
      }
    };
    getImprovementData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goal, surveyId]);

  useEffect(() => {
    if (!goal?.id) {
      return;
    }
    const getRecentSurvey = async () => {
      const result = await get(`/goals/${goal?.id}/surveys`);
      setRecentSurveys(result);
    };
    getRecentSurvey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goal]);

  useEffect(() => {
    const sId = surveyId || goal?.originalSurvey;
    if (!sId) {
      return;
    }
    const getOriginalSurvey = async () => {
      const result = await get(`/survey/${sId}`);
      setOriginalSurvey(result);
      setQuestions(getQuestionsForSurveyType(result?.surveyType || "long-term"));
    };
    getOriginalSurvey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goal]);

  useEffect(() => {
    if (!goalId) {
      return;
    }
    reloadGoal();
  }, [reloadGoal]);

  return (
    <ImprovementGoalContext.Provider value={{ goal, improvementData, recentSurveys, originalSurvey, questions, reloadGoal, setGoal }}>
      {children}
    </ImprovementGoalContext.Provider>
  );
};
